import { Booking, Operator } from "./model";
import { Currency } from "../Currencies/model";

/**
 * Operator-side pricing for the booking page, computed once for every card that
 * shows it (Overview pricing, Operator-tab pricing, Discounts & revenues) so the
 * figures can't disagree.
 *
 * Commission = client charter price − net charter fee, both in the operator's
 * currency. The client charter price is the customer's discounted charter price
 * (converted when the operator bills in another currency) unless ops have typed
 * one into the operator pricing modal. Previously the commission used only that
 * typed value, which is 0 until someone edits it — so a €3,848 charter with a
 * €3,316 net fee showed a commission of −€3,316.
 *
 * Extras check: extras the customer pays HELM in advance are owed on to the
 * operator, so the operator's extras should equal them. Extras paid locally are
 * settled at the base and appear on neither side.
 */
export type OperatorPricingView = {
  currencyCode: string;
  clientPrice: number;
  clientPriceSource: "customer" | "manual";
  netCharterFee: number;
  /** Where the net fee came from: ops' modal, the booking's own net fee (contract / commission), or the pricing snapshot's operator price. */
  netCharterFeeSource: "manual" | "booking" | "commission" | "snapshot";
  /** VAT and APA the client pays that pass straight through to the operator (crewed charters). */
  vat: number;
  apa: number;
  extras: number;
  totalPayable: number;
  commission: number;
  commissionPct: number | null;
  advanceExtras: { text?: string }[];
  /** Customer's in-advance extras, in the operator's currency. */
  customerAdvanceExtras: number;
  /** customer − operator, only when they disagree beyond rounding. */
  extrasMismatch: number | null;
  converted: boolean;
};

export const resolveOperatorCurrencyCode = (booking: Booking, operator?: Operator | null) =>
  booking?.operatorTransfer?.currencyCode ||
  booking?.pricing?.yachtCurrencyCode ||
  operator?.transferDetails?.currencyCode ||
  "EUR";

export function buildOperatorPricing(
  booking: Booking,
  currencies: Currency[] | undefined,
  operatorCurrencyCode: string,
): OperatorPricingView {
  const pricing = booking?.pricing;
  const offerCode = booking?.currency?.code || "EUR";
  const from = currencies?.find((c) => c.code === offerCode);
  const to = currencies?.find((c) => c.code === operatorCurrencyCode);
  const converted = offerCode !== operatorCurrencyCode;
  // Same rate maths as calculatePrice in PricingSection.
  const toOperator = (amount: number) =>
    !converted || !from?.rate || !to?.rate ? amount : Math.round(amount * (to.rate / from.rate) * 100) / 100;

  const amenities = booking?.offerYacht?.yacht?.amenities;
  const advanceExtras = (booking?.amenitiesDetails ?? [])
    .filter((am) => am.checked && !am.included)
    .map((am) => ({ ...amenities?.find((a) => a.id === am.id), ...am }) as any)
    .filter((am) => !am.paidLocally);

  const extrasSnapshot = pricing?.extrasPriceInOperatorCurrency ?? 0;
  const saved = booking?.operatorPricing;
  // The net fee: ops' modal first; then the booking's own net charter fee (set by
  // Confirm booking / the commission editor — Grauer: €46,560 less 15% = €39,576);
  // then client price less the commission amount; the pricing snapshot's operator
  // price last (for crewed bookings that is the LIST price, which read as a
  // negative commission).
  const netCcy = (booking?.netCharterFeeCurrency || offerCode).toUpperCase();
  const bookingNet = Number(booking?.netCharterFee) > 0
    ? (netCcy === operatorCurrencyCode.toUpperCase() ? Number(booking!.netCharterFee) : netCcy === offerCode.toUpperCase() ? toOperator(Number(booking!.netCharterFee)) : Number(booking!.netCharterFee))
    : null;
  const commissionNet = bookingNet == null && Number(booking?.commissionAmount) > 0 && Number(pricing?.clientPrice) > 0 ? toOperator(Number(pricing!.clientPrice) - Number(booking!.commissionAmount)) : null;
  const netCharterFee = saved && Number(saved.netCharterFee) > 0 ? Number(saved.netCharterFee) : bookingNet ?? commissionNet ?? (pricing?.operatorPayablePrice ?? 0) - extrasSnapshot;
  const netCharterFeeSource: OperatorPricingView["netCharterFeeSource"] = saved && Number(saved.netCharterFee) > 0 ? "manual" : bookingNet != null ? "booking" : commissionNet != null ? "commission" : "snapshot";
  const vat = toOperator(Number(pricing?.vatPrice) || 0);
  const apa = toOperator(Number(pricing?.apaPrice) || 0);
  // Saved extras win — before, the card overwrote them with the pricing snapshot,
  // so editing extras in the modal never showed.
  const extras = saved && saved.extras != null ? Number(saved.extras) || 0 : extrasSnapshot;
  const manualClient = saved && Number(saved.clientPrice) > 0 ? Number(saved.clientPrice) : null;
  const clientPrice = manualClient ?? toOperator(pricing?.clientPrice ?? 0);
  const commission = Math.round((clientPrice - netCharterFee) * 100) / 100;

  const customerAdvanceExtras = toOperator(advanceExtras.reduce((t: number, am: any) => t + (Number(am.price) || 0), 0));
  const diff = Math.round((customerAdvanceExtras - extras) * 100) / 100;
  // Allow for conversion rounding: the larger of 1 unit or 0.5%.
  const tolerance = Math.max(1, Math.abs(customerAdvanceExtras) * 0.005);

  return {
    currencyCode: operatorCurrencyCode,
    clientPrice,
    clientPriceSource: manualClient != null ? "manual" : "customer",
    netCharterFee,
    netCharterFeeSource,
    vat,
    apa,
    extras,
    totalPayable: netCharterFee + extras + vat + apa,
    commission,
    commissionPct: clientPrice > 0 ? Math.round((commission / clientPrice) * 1000) / 10 : null,
    advanceExtras,
    customerAdvanceExtras,
    extrasMismatch: Math.abs(diff) > tolerance ? diff : null,
    converted,
  };
}
